import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { StateMessage } from '@/components/ui/controls';
import { Badge, Card, Field, PageHeader, PreviewBanner } from '@/components/ui/page';
import { btn } from '@/components/ui/styles';
import { LINK_METHOD_LABELS, NOTE_TAKER_LABELS, callById } from '@/mocks/calls';
import { leadSearchLink } from '@/mocks/shared';
import { formatDateTime, initials } from '@/utils/format';

import { NOTE_TONES, leadName, staffName } from './call-helpers';

const back = { to: '/calls', label: 'Calls' };

/**
 * One call (CALL-1…4, planned for V2): who took part and why the call was linked to
 * this lead (CALL-2), the note taker's summary and action items (CALL-4), and a
 * transcript excerpt. Preview: the call is made up, and "Add as follow-up" keeps nothing.
 * On a phone the two columns stack (UI-1).
 */
const CallPage = () => {
  const { id = '' } = useParams();
  const call = callById(id);
  const [added, setAdded] = useState<Record<string, boolean>>({});

  if (!call) {
    return (
      <div>
        <PageHeader title="Call" back={back} />
        <StateMessage title="Call not found">
          There is no call with this id.{' '}
          <Link to="/calls" className={btn.link}>
            Back to calls
          </Link>
        </StateMessage>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        back={back}
        title={call.title}
        description={`${formatDateTime(call.startedAt)} · ${call.durationMin} min · ${leadName(call.leadEmail)} with ${staffName(call.staffId)}`}
      />
      <PreviewBanner requirements={['CALL-1…4', 'Q14']}>Planned for V2. Not connected to data yet: this call is made up.</PreviewBanner>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <div className="space-y-6">
          <Card title="Summary" aside={<Badge tone={NOTE_TONES[call.noteTaker]}>{NOTE_TAKER_LABELS[call.noteTaker]}</Badge>}>
            {call.summary.length ? (
              <ul className="list-disc space-y-1 pl-5 text-sm leading-relaxed text-fg">
                {call.summary.map((s) => (
                  <li key={s}>{s}</li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-fg-faint">
                {call.noteTaker === 'processing' ? 'The note taker is still writing the summary.' : 'No notes: the note taker was not on this call.'}
              </p>
            )}
          </Card>

          <Card title="Action items">
            {call.actionItems.length ? (
              <ul className="space-y-2">
                {call.actionItems.map((a) => (
                  <li key={a.id} className="flex items-center justify-between gap-3 rounded-lg bg-surface-inset px-3 py-2 text-sm">
                    <span className="min-w-0 text-fg">{a.text}</span>
                    {added[a.id] ? (
                      <span className="shrink-0 text-xs text-fg-muted">Follow-up added</span>
                    ) : (
                      <button type="button" className={btn.quiet} onClick={() => setAdded((s) => ({ ...s, [a.id]: true }))}>
                        Add as follow-up
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-fg-faint">No action items.</p>
            )}
            {Object.keys(added).length > 0 && <p className="mt-2 text-xs text-fg-faint">Preview: follow-ups added here are not saved.</p>}
          </Card>

          <Card title="Transcript excerpt">
            {call.transcript.length ? (
              <ol className="space-y-3">
                {call.transcript.map((t) => (
                  <li key={t.at} className="grid grid-cols-[48px_1fr] gap-3 text-sm">
                    <span className="tabular-nums text-fg-faint">{t.at}</span>
                    <div>
                      <p className="text-xs text-fg-muted">{t.speaker}</p>
                      <p className="text-fg">{t.text}</p>
                    </div>
                  </li>
                ))}
              </ol>
            ) : (
              <p className="text-sm text-fg-faint">No transcript for this call.</p>
            )}
          </Card>
        </div>

        <div className="space-y-6">
          <Card title="Linked to">
            <p className="text-sm text-fg-strong">{leadName(call.leadEmail)}</p>
            <Link to={leadSearchLink(call.leadEmail)} className={`${btn.link} inline-flex min-h-11 items-center [overflow-wrap:anywhere] md:min-h-0`}>
              {call.leadEmail}
            </Link>
            <dl className="mt-3">
              <Field label="Matched by">{LINK_METHOD_LABELS[call.link.method]}</Field>
            </dl>
            <p className="mt-1 text-sm text-fg-muted">{call.link.detail}</p>
            <p className="mt-3 text-xs text-fg-faint">Calls are linked automatically, even when the client joins with another email (CALL-2).</p>
          </Card>

          <Card title="Participants">
            <ul className="space-y-3">
              {call.participants.map((p) => (
                <li key={p.email} className="flex items-center gap-3 text-sm">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-surface-inset text-xs text-fg-muted">
                    {initials(p.name, p.email)}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-fg">{p.name}</p>
                    <p className="truncate text-xs text-fg-muted">{p.email}</p>
                  </div>
                  <Badge tone={p.side === 'team' ? 'brand' : 'neutral'}>{p.side === 'team' ? 'Team' : 'Client'}</Badge>
                </li>
              ))}
            </ul>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default CallPage;
